/* eslint-disable @typescript-eslint/no-explicit-any */
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { hasValidDuration } from "@/lib/formatUtils";
import { hasSpecialOffer } from "@/special-offers";
import { Calculator, ChevronDown, Clock, Download, Euro, FileText, TrendingDown, TrendingUp, Users, X, ArrowRight, TableProperties } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import PaymentModalitySection from "@/components/PaymentModalitySection";
import { getTjmForMode, resolvePackConfig, getComparisonModel, calculateHybridPrestationsBreakdown, calculateHybridDurationMonths } from "@/utils/packAilixPricing";
import type { PricingMode } from "@/utils/packAilixPricing";
import { findOptionById, CORE_OPTION_IDS, normalizeOptionId } from "@/utils/optionUtils";
import type { MissionRoiByMissionId } from "@/utils/realtimeMissionRoi";
import { buildFlatMissionMap, countLogicalMissions, deduplicateSelectedMissionItems, isSubMission } from "@/utils/subMissionUtils";
import type { Mission } from "@/services/clientDataService";
import { useMemo } from "react";

interface AnnualCostsResult {
  totalAnnualCost: number;
  totalAnnualCostWithTemporality: number;
  totalCostWithProjectDuration: number;
  projectDurationYears: number;
}

export interface NavigationSummaryContentProps {
  pricing: { total: number; discounts: Array<{ name: string; amount: number }> };
  estimatedDuration?: string;
  selectedItems: Array<{ id: string; type: "mission" | "option"; quantity: number }>;
  allMissions: Array<{ id: string; title: string; duration?: string; duration_days?: number; price?: number; [key: string]: unknown }>;
  allOptions: Array<{ id: string; title: string }>;
  dynamicOptions?: Array<{ id: string; option_id?: string; title: string; price: number; is_included: boolean }>;
  roiEnabled: boolean;
  roi3Years: number;
  roiAnnual: number;
  clientId?: string;
  clientData: any;
  pricingMode: PricingMode;
  setPricingMode: React.Dispatch<React.SetStateAction<PricingMode>>;
  pricingNbDevelopers?: number;
  enabledPrestations?: Record<string, boolean>;
  packAilixEligibility: { eligible: boolean; progress: number; remainingAmount: number };
  calculatedEconomies: { annualSavings: number; savings3Years: number };
  totalEtpSaved: number;
  annualCosts: AnnualCostsResult | null;
  includedOptionsCost: number;
  onRemoveItem?: (id: string, type: "mission" | "option") => void;
  onSign?: () => void;
  onExport?: () => void;
  onExportMissionsPdf?: () => void;
  /** ROI par mission et par mode de tarification. */
  roiByMissionId?: MissionRoiByMissionId;
}

/**
 * Contenu métier de la synthèse devis, partagé entre desktop (panneau latéral) et mobile (drawer).
 */
export function NavigationSummaryContent({
  pricing,
  estimatedDuration,
  selectedItems,
  allMissions,
  allOptions,
  dynamicOptions = [],
  roiEnabled,
  roi3Years,
  roiAnnual,
  clientId,
  clientData,
  pricingMode,
  setPricingMode,
  pricingNbDevelopers = 2,
  enabledPrestations,
  packAilixEligibility,
  calculatedEconomies,
  totalEtpSaved,
  annualCosts,
  includedOptionsCost,
  onRemoveItem,
  onSign,
  onExport,
  onExportMissionsPdf,
  roiByMissionId,
}: NavigationSummaryContentProps) {
  // Déduplication : retirer les parents groupe si leurs sous-missions sont aussi sélectionnées.
  // F2 (DEVIS-MODALITY-LOOP-001) : mémoïser la chaîne pour stabiliser selectedMissionsForPayment
  // (passé en prop à usePaymentModalityState) — une référence instable à chaque render alimentait
  // la boucle de rendu → flood réseau → ban IP. Logique de calcul strictement inchangée.
  const deduplicatedItems = useMemo(
    () => deduplicateSelectedMissionItems(selectedItems, allMissions),
    [selectedItems, allMissions]
  );
  const selectedMissionItems = useMemo(
    () => deduplicatedItems.filter((item) => item.type === "mission"),
    [deduplicatedItems]
  );
  const selectedOptionItemsRaw = deduplicatedItems.filter((item) => item.type === "option");

  // Comptage logique : chaque groupe = 1, chaque standalone = 1
  const logicalMissionCount = useMemo(
    () => countLogicalMissions(selectedItems, allMissions),
    [selectedItems, allMissions]
  );

  const missionMap = useMemo(() => buildFlatMissionMap(allMissions), [allMissions]);
  const selectedMissionsForPayment = useMemo(
    () => selectedMissionItems
      .map((item) => missionMap.get(item.id))
      .filter(Boolean)
      .map((mission) => ({
        ...(mission as any),
        duration_days:
          (mission as any)?.duration_days ||
          parseFloat((((mission as any)?.duration || "0").split(" ")[0] || "0").replace(",", ".")) ||
          0,
        price: (mission as any)?.price || 0,
      })),
    [selectedMissionItems, missionMap]
  );

  // Expert IA : afficher dans les options si actif dans la config
  const packConfig = clientData?.pack_ailix_config;
  const resolvedConfig = resolvePackConfig(packConfig);
  const comparisonModel = getComparisonModel(packConfig);
  const isHybridMode = comparisonModel === 'hybrid' && packAilixEligibility.eligible;
  const expertIaPresta = resolvedConfig.prestations?.expert_ia;
  const isExpertIaActive = expertIaPresta?.enabled_by_default
    && !(pricingMode === "a_la_carte" && comparisonModel === "legacy");

  // En mode hybride, exclure les options core (PM, MOE, Expert IA) de "Options sélectionnées"
  // et "Options incluses" car elles apparaissent dans la section dédiée "Prestations incluses"
  const selectedOptionItems = isHybridMode
    ? selectedOptionItemsRaw.filter(item => !CORE_OPTION_IDS.has(normalizeOptionId(item.id)))
    : selectedOptionItemsRaw;

  const includedOptions = dynamicOptions.filter(opt => {
    if (!opt.is_included) return false;
    if (isHybridMode) {
      const normalizedId = normalizeOptionId(opt.option_id || opt.id);
      if (CORE_OPTION_IDS.has(normalizedId)) return false;
    }
    return true;
  });

  // Calculer les prestations hybrides pour affichage
  let hybridPrestationsData: Record<string, { label: string; joursParMois: number; joursTotalProjet: number; tjm: number; coutTotal: number }> | null = null;
  let hybridDurationMonths = 0;

  if (isHybridMode && selectedMissionsForPayment.length > 0) {
    const totalDaysForHybrid = selectedMissionsForPayment.reduce(
      (sum, m) => sum + ((m as any).duration_days || 0), 0
    );
    if (totalDaysForHybrid > 0) {
      const nbDevs = pricingNbDevelopers > 0 ? pricingNbDevelopers : 2;
      hybridDurationMonths = calculateHybridDurationMonths(totalDaysForHybrid, nbDevs);
      hybridPrestationsData = calculateHybridPrestationsBreakdown(
        totalDaysForHybrid, nbDevs, pricingMode, packConfig,
        clientData?.tjm_moe ?? undefined,
        enabledPrestations,
      );
    }
  }

  const totalDiscountAmount = pricing?.discounts?.reduce((sum, d) => sum + (d.amount || 0), 0) || 0;
  const effectiveSubtotal = (pricing?.total || 0) + totalDiscountAmount;
  const totalDiscountPercentage = effectiveSubtotal > 0 ? Math.round((totalDiscountAmount / effectiveSubtotal) * 100) : 0;

  return (
    <>
      <div className="flex-1 overflow-auto p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 border border-border shadow-sm">
            <div className="text-2xl font-bold text-primary">{logicalMissionCount}</div>
            <div className="text-sm text-primary/70 font-medium">Missions sélectionnées</div>
          </div>

          {hasValidDuration(estimatedDuration) && (
            <div className="bg-white rounded-xl p-4 border border-border shadow-sm">
              <div className="text-2xl font-bold text-accent">{estimatedDuration}</div>
              <div className="text-sm text-accent/70 font-medium">Durée estimée</div>
            </div>
          )}

          {roiEnabled && roi3Years > 0 && (
            <>
              <div className="bg-white rounded-xl p-4 border border-border shadow-sm">
                <div className="text-2xl font-bold text-green-700">{roi3Years}%</div>
                <div className="text-sm text-green-600 font-medium">ROI sur 3 ans</div>
              </div>
              <div className="bg-gradient-to-br from-orange-100 to-orange-50 rounded-xl p-4 border border-orange-300 shadow-sm">
                <div className="text-2xl font-bold text-orange-700">{roiAnnual}%</div>
                <div className="text-sm text-orange-600 font-medium">ROI annuel</div>
              </div>
            </>
          )}

          {totalEtpSaved > 0 && (
            <div className="bg-gradient-to-br from-purple-100 to-purple-50 rounded-xl p-4 border border-purple-300 shadow-sm">
              <div className="text-2xl font-bold text-purple-700">{totalEtpSaved.toFixed(2)}</div>
              <div className="text-sm text-purple-600 font-medium">ETP économisés</div>
            </div>
          )}

          <div className="bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl p-4 border border-blue-300 shadow-sm">
            <div
              className={cn(
                "text-2xl font-bold",
                pricingMode !== "a_la_carte" && packAilixEligibility.eligible ? "text-blue-600" : "text-gray-600"
              )}
            >
              {getTjmForMode(packAilixEligibility.eligible ? pricingMode : "a_la_carte", clientData?.pack_ailix_config, clientData?.tjm)}€
            </div>
            <div className="text-sm text-blue-600 font-medium">TJM</div>
          </div>
        </div>

        {clientData?.pack_ailix_enabled === true && (
          <div className="px-6 py-4 border-b bg-gradient-to-r from-blue-50/50 to-transparent space-y-3">
            {clientData?.special_offer_enabled && clientData?.id && hasSpecialOffer(clientData.id) && (
              <div className="flex justify-center">
                <Link
                  to={clientId ? `/${clientId}/offre-speciale` : "/offre-speciale"}
                  className="group inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-300 rounded-full text-sm font-semibold text-blue-700 hover:from-blue-100 hover:to-blue-200"
                >
                  <span>Voir le détail des offres avec le pack Ailix</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            )}

            <div className="flex rounded-lg border border-border bg-white p-1 gap-1">
              <button
                onClick={() => setPricingMode("a_la_carte")}
                className={cn(
                  "flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200",
                  pricingMode === "a_la_carte" ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                )}
              >
                À la carte
              </button>
              <button
                onClick={() => packAilixEligibility.eligible && setPricingMode("pack_ailix_1an")}
                disabled={!packAilixEligibility.eligible}
                className={cn(
                  "flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200",
                  !packAilixEligibility.eligible
                    ? "text-gray-300 cursor-not-allowed"
                    : pricingMode === "pack_ailix_1an"
                    ? "bg-blue-600 text-white"
                    : "text-blue-600 hover:bg-blue-50"
                )}
              >
                Pack 1 an
              </button>
              <button
                onClick={() => packAilixEligibility.eligible && setPricingMode("pack_ailix_2ans")}
                disabled={!packAilixEligibility.eligible}
                className={cn(
                  "flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200",
                  !packAilixEligibility.eligible
                    ? "text-gray-300 cursor-not-allowed"
                    : pricingMode === "pack_ailix_2ans"
                    ? "bg-blue-600 text-white"
                    : "text-blue-600 hover:bg-blue-50"
                )}
              >
                Pack 2+ ans
              </button>
            </div>

          </div>
        )}

        <PaymentModalitySection
          selectedMissions={selectedMissionsForPayment as any}
          totalCost={pricing.total || 0}
          clientId={clientId || ""}
          pricingMode={pricingMode}
          packConfig={clientData?.pack_ailix_config ?? null}
          clientTjm={clientData?.tjm}
          clientTjmMoe={clientData?.tjm_moe}
        />

        <div className="space-y-3">
          <h3 className="font-medium text-primary">Analyse financière</h3>
          <div className="bg-white rounded-xl border border-border p-4 space-y-4 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Investissement initial</div>
                <div className="text-lg font-semibold">{pricing.total.toLocaleString()}€ HT</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Total TTC</div>
                <div className="text-lg font-semibold">{(pricing.total * 1.2).toLocaleString()}€</div>
              </div>
            </div>

            {annualCosts && (
              <div className="border-t pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Coûts annuels (moyen)</div>
                  <div className="text-base text-orange-600">{Math.round(annualCosts.totalAnnualCostWithTemporality / annualCosts.projectDurationYears).toLocaleString()}€/an</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Coût sur {annualCosts.projectDurationYears} ans</div>
                  <div className="text-base">{annualCosts.totalCostWithProjectDuration.toLocaleString()}€</div>
                </div>
              </div>
            )}

            {pricing.discounts.length > 0 && (
              <div className="border-t pt-4 space-y-2">
                <div className="text-sm text-muted-foreground font-medium">Remises appliquées</div>
                {pricing.discounts.map((discount, index) => (
                  <div key={`${discount.name}-${index}`} className="flex justify-between text-sm">
                    <span className={discount.name.includes("exceptionnelle") ? "text-orange-700" : "text-green-700"}>{discount.name}</span>
                    <span className="font-medium">-{discount.amount.toLocaleString()}€</span>
                  </div>
                ))}
                <div className="flex justify-between items-center text-sm font-semibold border-t pt-2 bg-green-50/50 px-2 py-1 rounded">
                  <span className="text-green-800">Total des remises {totalDiscountPercentage > 0 ? `(${totalDiscountPercentage}%)` : ""}</span>
                  <span className="text-green-600">
                    -{pricing.discounts.reduce((sum, discount) => sum + (discount.amount || 0), 0).toLocaleString()}€
                  </span>
                </div>
              </div>
            )}

            {roiEnabled && calculatedEconomies.annualSavings > 0 && (
              <div className="border-t pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">Économies annuelles</div>
                  <div className="text-lg font-semibold text-green-600">{calculatedEconomies.annualSavings.toLocaleString()}€</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Économies sur 3 ans</div>
                  <div className="text-lg font-semibold text-green-600">{calculatedEconomies.savings3Years.toLocaleString()}€</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <MissionListSection
          selectedMissionItems={selectedMissionItems}
          missionMap={missionMap}
          roiByMissionId={roiByMissionId}
          pricingMode={pricingMode}
          onRemoveItem={onRemoveItem}
        />

        {selectedOptionItems.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-medium text-primary">Options sélectionnées</h3>
            <div className="space-y-2">
              {selectedOptionItems.map((item, index) => {
                const option = findOptionById(allOptions as Array<{ id: string; option_id?: string; title: string }>, item.id);
                // Fallback : chercher dans dynamicOptions si allOptions ne résout pas le label
                const dynamicOption = !option?.title
                  ? dynamicOptions.find(d => d.id === item.id || d.option_id === item.id)
                  : null;
                const optionDisplayNames: Record<string, string> = {
                  "design-moe": "Maîtrise d'œuvre, Maquettes & Design",
                };
                const displayName = optionDisplayNames[item.id] || option?.title || dynamicOption?.title || item.id;
                const optionPrice = dynamicOption?.price ?? (option as any)?.price;

                return (
                  <div key={`${item.id}-${index}`} className="flex items-center justify-between p-3 bg-white rounded-lg border border-border shadow-sm">
                    <span>{displayName}</span>
                    <div className="flex items-center gap-2">
                      {optionPrice != null && optionPrice > 0 && (
                        <span className="text-sm text-muted-foreground">{optionPrice.toLocaleString()}€</span>
                      )}
                      {item.quantity > 1 && <Badge variant="outline">{item.quantity}</Badge>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Options incluses (facturées automatiquement) */}
        {(includedOptions.length > 0 || (isExpertIaActive && !isHybridMode)) && (
          <div className="space-y-3">
            <h3 className="font-medium text-primary">Options incluses</h3>
            <div className="space-y-2">
              {includedOptions.map((opt, index) => (
                  <div key={`included-${opt.id}-${index}`} className="flex items-center justify-between p-3 bg-blue-50/50 rounded-lg border border-blue-200 shadow-sm">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">Incluse</Badge>
                      <span>{opt.title || opt.id}</span>
                    </div>
                    {opt.price > 0 && (
                      <span className="text-sm font-medium">{opt.price.toLocaleString()}€</span>
                    )}
                  </div>
                ))}
              {isExpertIaActive && expertIaPresta && !isHybridMode && (
                <div className="flex items-center justify-between p-3 bg-blue-50/50 rounded-lg border border-blue-200 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">Incluse</Badge>
                    <span>{expertIaPresta.label}</span>
                  </div>
                  <span className="text-sm font-medium">
                    {(expertIaPresta.jours_par_mois * (pricingMode === "pack_ailix_2ans" ? expertIaPresta.tjm_2ans : expertIaPresta.tjm_1an)).toLocaleString()}€/mois
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prestations incluses (mode hybride Pack Ailix) */}
        {hybridPrestationsData && Object.keys(hybridPrestationsData).length > 0 && (
          <div className="space-y-3">
            <h3 className="font-medium text-primary">Prestations incluses</h3>
            <p className="text-xs text-muted-foreground">
              Mensualisées sur {hybridDurationMonths} mois
            </p>
            <div className="space-y-2">
              {Object.entries(hybridPrestationsData).map(([key, presta]) => (
                <div key={`presta-${key}`} className="flex items-center justify-between p-3 bg-blue-50/50 rounded-lg border border-blue-200 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-300">
                      Incluse
                    </Badge>
                    <div>
                      <span>{presta.label}</span>
                      <div className="text-xs text-muted-foreground">
                        {presta.joursParMois}j/mois x {hybridDurationMonths} mois
                      </div>
                    </div>
                  </div>
                  <span className="text-sm font-medium">{presta.coutTotal.toLocaleString()}€</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="p-6 border-t bg-secondary/50">
        <div className="flex gap-3">
          <Button variant="hero" className="flex-1" onClick={onSign}>
            <FileText className="w-4 h-4 mr-2" />
            Envoyer la demande
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="px-6 bg-white/90 text-foreground">
                <Download className="w-4 h-4 mr-2" />
                Télécharger
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onExport}>
                <FileText className="w-4 h-4 mr-2" />
                Télécharger le devis
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportMissionsPdf}>
                <TableProperties className="w-4 h-4 mr-2" />
                Télécharger un récapitulatif
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </>
  );
}

// ============================================================================
// Liste hiérarchique des missions (standalone + groupes avec sous-missions)
// ============================================================================

function MissionListSection({
  selectedMissionItems,
  missionMap,
  roiByMissionId,
  pricingMode,
  onRemoveItem,
}: {
  selectedMissionItems: Array<{ id: string; type: string; quantity: number }>;
  missionMap: Map<string, Mission>;
  roiByMissionId?: MissionRoiByMissionId;
  pricingMode: PricingMode;
  onRemoveItem?: (id: string, type: "mission" | "option") => void;
}) {
  // Séparer missions standalone et sous-missions groupées par parent
  const { standalone, groups } = useMemo(() => {
    const standaloneItems: typeof selectedMissionItems = [];
    const byParent = new Map<string, typeof selectedMissionItems>();

    for (const item of selectedMissionItems) {
      const mission = missionMap.get(item.id) as Mission | undefined;
      if (mission && isSubMission(mission)) {
        const parentId = mission.parent_mission_id!;
        if (!byParent.has(parentId)) byParent.set(parentId, []);
        byParent.get(parentId)!.push(item);
      } else {
        standaloneItems.push(item);
      }
    }

    // Trier les groupes dans l'ordre d'apparition de la première sous-mission
    const groupEntries = Array.from(byParent.entries());

    return { standalone: standaloneItems, groups: groupEntries };
  }, [selectedMissionItems, missionMap]);

  return (
    <div className="space-y-3">
      <h3 className="font-medium text-primary">Missions sélectionnées</h3>
      <div className="space-y-2">
        {/* Missions standalone */}
        {standalone.map((item, index) => (
          <MissionRow
            key={`standalone-${item.id}-${index}`}
            item={item}
            missionMap={missionMap}
            roiByMissionId={roiByMissionId}
            pricingMode={pricingMode}
            onRemoveItem={onRemoveItem}
          />
        ))}

        {/* Groupes : header parent + sous-missions indentées */}
        {groups.map(([parentId, subItems]) => {
          const parent = missionMap.get(parentId);
          const parentTitle = parent?.title ?? "Groupe";
          return (
            <div key={`group-${parentId}`} className="space-y-1">
              {/* Header du groupe parent */}
              <div className="flex items-center gap-2 px-3 py-2 bg-primary/5 rounded-lg border border-primary/15">
                <span className="text-sm font-semibold text-primary truncate">{parentTitle}</span>
                <Badge variant="outline" className="text-[10px] shrink-0 border-primary/25 text-primary/70">
                  {subItems.length} sous-mission{subItems.length > 1 ? "s" : ""}
                </Badge>
              </div>
              {/* Sous-missions indentées */}
              <div className="ml-3 border-l-2 border-primary/15 pl-2 space-y-1">
                {subItems.map((item, index) => (
                  <MissionRow
                    key={`sub-${item.id}-${index}`}
                    item={item}
                    missionMap={missionMap}
                    roiByMissionId={roiByMissionId}
                    pricingMode={pricingMode}
                    onRemoveItem={onRemoveItem}
                    compact
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Ligne de mission individuelle (standalone ou sous-mission). */
function MissionRow({
  item,
  missionMap,
  roiByMissionId,
  pricingMode,
  onRemoveItem,
  compact,
}: {
  item: { id: string; type: string; quantity: number };
  missionMap: Map<string, Mission>;
  roiByMissionId?: MissionRoiByMissionId;
  pricingMode: PricingMode;
  onRemoveItem?: (id: string, type: "mission" | "option") => void;
  compact?: boolean;
}) {
  const mission = missionMap.get(item.id);
  const displayName = mission?.title || item.id;
  const missionRoiData: any = (mission as any)?.roi_data || (mission as any)?.complete_json?.roi_data || {};
  const etpSaved = missionRoiData?.etp_economise ?? missionRoiData?.etp_saved ?? missionRoiData?.savings?.etp ?? 0;
  const dynamicRoi = roiByMissionId && mission ? roiByMissionId[mission.id]?.[pricingMode]?.roiPct36m : undefined;
  const roi3YearsPercent = dynamicRoi != null
    ? Math.round(dynamicRoi)
    : (missionRoiData?.roi_pct_36m ?? missionRoiData?.roi_3_years ?? missionRoiData?.roi_3ans ?? 0);

  return (
    <div className={cn(
      "bg-white rounded-lg border border-border shadow-sm",
      compact ? "p-2" : "p-3"
    )}>
      <div className="flex items-center justify-between gap-2">
        {onRemoveItem && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveItem(item.id, "mission");
            }}
            aria-label={`Supprimer mission ${displayName}`}
            className="p-1 rounded bg-red-100 text-red-500 hover:bg-red-200"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <span className={cn("font-medium truncate flex-1", compact && "text-sm")}>{displayName}</span>
        <div className="flex items-center gap-2 text-xs shrink-0">
          {roi3YearsPercent > 0 && (
            <span className="flex items-center gap-1 text-emerald-600">
              <TrendingUp className="w-3 h-3" />
              {roi3YearsPercent}%
            </span>
          )}
          {etpSaved > 0 && (
            <span className="flex items-center gap-1 text-amber-600">
              <Users className="w-3 h-3" />
              {Number(etpSaved).toFixed(2)} ETP
            </span>
          )}
          {item.quantity > 1 && <Badge variant="secondary">{item.quantity}</Badge>}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Wrapper desktop (panneau latéral avec overlay)
// ============================================================================

interface NavigationSummaryPanelProps extends NavigationSummaryContentProps {
  isExpanded: boolean;
  onClose: () => void;
  hasSelection: boolean;
}

export function NavigationSummaryPanel({
  isExpanded,
  onClose,
  hasSelection,
  ...contentProps
}: NavigationSummaryPanelProps) {
  if (!hasSelection || !contentProps.pricing) return null;

  return (
    <>
      <div
        className={cn(
          "fixed inset-0 bg-black/20 backdrop-blur-md z-40 transition-all duration-500 ease-out",
          isExpanded ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
      />

      <div
        className={cn(
          "fixed right-0 top-16 bottom-0 w-full md:w-1/2 bg-gradient-card border-l border-border shadow-2xl z-50",
          "transform transition-all duration-500 ease-out",
          isExpanded ? "translate-x-0 shadow-elegant" : "translate-x-full"
        )}
      >
        <div className="h-full flex flex-col">
          <div className="p-6 border-b bg-secondary/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calculator className="w-5 h-5 text-primary" />
                <h2 className="text-xl font-semibold">Synthèse du projet</h2>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
                <ChevronDown className="w-4 h-4 rotate-90" />
              </Button>
            </div>
          </div>

          <NavigationSummaryContent {...contentProps} />
        </div>
      </div>
    </>
  );
}
